import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Stack,
  Paper,
  ShipsIcon,
  StorageIcon,
  Tab,
  Tabs,
  TrendingUpIcon
} from '@esgian/esgianui';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  OperatorEmissionsSection,
  OperatorCiiRatingSection,
  OperatorActivitySection
} from '@components/Sections/ShipAnalytics/OperatorProfileSection/SubSections';
import OperatorOverviewSection from './SubSections/OperatorOverviewSection';
import TradeRouteSection from '@components/Sections/ShipAnalytics/OperatorProfileSection/SubSections/TradeRouteSection';
import { TextWithTooltipIcon } from '@components';
import { useSegment } from '@hooks';
import KpiBox from '@components/Display/KpiBox';
import { formatNumberReadable, parseNumberToSignificant } from '@helpers';
import { getOperatorCiiRatingData, getOperatorPercentageCo2Emission } from '@api';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import CompanyPortStatisticsSection from '@components/Sections/ShipAnalytics/CompanyPortStatisticsSection';
import FutureScheduleSection from '@components/Sections/ShipAnalytics/FutureScheduleSection/FutureScheduleSection';
import { useFutureScheduleAvailability } from '@hooks/useFutureScheduleAvailability';

function OperatorProfileSection({ overviewDataQuery = {}, renderedOverviewMap }) {
  const [tabSection, setTabSection] = useState(1);
  const [isFutureScheduleAvailable, setIsFutureScheduleAvailable] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const { isRoRo, segment } = useSegment();
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const operatorType = useSelector(getOperatorType);
  const operatorId = Number(params.id);

  const operatorCiiRatingQuery = useQuery({
    queryKey: [
      'operatorCiiRating',
      {
        OperatorId: operatorId,
        SegmentTypeId: segment.id,
        OperatorTypeId: operatorType,
        CommercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!operatorId && !!segment.id,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getOperatorCiiRatingData(queryKey[1], signal)
        .then((resp) => resp)
        .catch(() => {
          return null;
        })
  });

  const operatorCo2EmissionQuery = useQuery({
    queryKey: [
      'operatorCo2Emission',
      {
        OperatorId: operatorId,
        SegmentTypeId: segment.id,
        OperatorTypeId: operatorType,
        CommercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!operatorId && !!segment.id,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getOperatorPercentageCo2Emission(queryKey[1], signal)
        .then((resp) => resp)
        .catch(() => {
          return null;
        })
  });

  const handleSectionChange = (_, val) => {
    if (!operatorCo2EmissionQuery.data && val === 3) {
      return;
    }
    if (!operatorCiiRatingQuery.data && val === 4) {
      return;
    }
    if (!isFutureScheduleAvailable && val === 7) {
      return;
    }
    setTabSection(val);
    searchParams.set('section', val);
    setSearchParams(searchParams);
  };

  useEffect(() => {
    setTabSection(parseInt(searchParams.get('section') || 1));
  }, []);

  const {
    averageAge,
    averageCapacity,
    fleetSize,
    totalCapacity,
    operatorShortName,
    operatorLongName
  } = overviewDataQuery.data || {};

  let imoList = [];
  if (overviewDataQuery.data) {
    imoList = overviewDataQuery.data.operatorVessels.map((vessel) => vessel.imo);
  }

  const futurePortCallsQuery = useFutureScheduleAvailability({
    imoList,
    segmentId: segment.id,
    entityId: params.id,
    setIsAvailable: setIsFutureScheduleAvailable
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          spacing={1} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          <KpiBox
            loading={overviewDataQuery.isFetching}
            icon={<ShipsIcon />}
            value={fleetSize ? fleetSize : ''}
            title={'Fleet Size'}
            subtitle={'Vessels'}
          />
          <KpiBox
            loading={overviewDataQuery.isFetching}
            icon={<TrendingUpIcon />}
            value={averageAge ? parseNumberToSignificant(averageAge) : ''}
            title={'Average Age'}
            subtitle={'Years'}
          />
          <KpiBox
            loading={overviewDataQuery.isFetching}
            icon={<StorageIcon />}
            value={averageCapacity ? formatNumberReadable(parseInt(averageCapacity)) : '-'}
            title={'Average Capacity'}
            subtitle={isRoRo ? 'CEU' : 'DWT'}
          />
          <KpiBox
            loading={overviewDataQuery.isFetching}
            icon={<StorageIcon />}
            value={totalCapacity ? formatNumberReadable(totalCapacity) : ''}
            title={'Total Capacity'}
            subtitle={isRoRo ? 'CEU' : 'DWT'}
          />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Tabs value={tabSection} onChange={handleSectionChange} variant="scrollable">
            <Tab value={1} label="Overview" />
            <Tab value={2} label="Activity" />
            <Tab
              sx={{ cursor: operatorCo2EmissionQuery.data ? 'pointer' : 'default' }}
              disableRipple={!operatorCo2EmissionQuery.data}
              value={3}
              id="vessel-cii-rating-tab"
              label={
                !operatorCo2EmissionQuery.data ? (
                  <TextWithTooltipIcon
                    color={'text.secondary'}
                    label={'CO₂ Emission'}
                    tooltipText={'Insufficient data to compute CO₂ emissions'}
                  />
                ) : (
                  'CO₂ Emission'
                )
              }
            />
            <Tab
              sx={{ cursor: operatorCiiRatingQuery.data ? 'pointer' : 'default' }}
              disableRipple={!operatorCiiRatingQuery.data}
              value={4}
              label={
                !operatorCiiRatingQuery.data ? (
                  <TextWithTooltipIcon
                    color={'text.secondary'}
                    label={'CII RATING'}
                    tooltipText={'Insufficient data to compute CII'}
                  />
                ) : (
                  'Cii rating'
                )
              }
            />
            <Tab value={5} label="COMMERCIAL FOOTPRINT" />
            <Tab value={6} label="PORT STATISTICS" />
            {isRoRo && (
              <Tab
                value={7}
                label={
                  isFutureScheduleAvailable ? (
                    'FUTURE SCHEDULE'
                  ) : (
                    <TextWithTooltipIcon
                      color={'text.secondary'}
                      label={'FUTURE SCHEDULE'}
                      tooltipText={'Schedule not available'}
                    />
                  )
                }
                sx={{ cursor: isFutureScheduleAvailable ? 'pointer' : 'default' }}
                disableRipple={!isFutureScheduleAvailable}
              />
            )}
          </Tabs>
        </Paper>
      </Grid>
      {tabSection === 1 && (
        <OperatorOverviewSection
          overviewDataQuery={overviewDataQuery}
          renderedOverviewMap={renderedOverviewMap}
        />
      )}
      {tabSection === 2 && <OperatorActivitySection operatorShortName={operatorShortName} />}
      {tabSection === 3 && (
        <OperatorEmissionsSection
          operatorCo2EmissionQuery={operatorCo2EmissionQuery}
          operatorShortName={operatorShortName}
        />
      )}
      {tabSection === 4 && (
        <OperatorCiiRatingSection
          operatorCiiRatingQuery={operatorCiiRatingQuery}
          operatorLongName={operatorLongName}
          operatorShortName={operatorShortName}
        />
      )}
      {tabSection === 5 && <TradeRouteSection />}
      {tabSection === 6 && (
        <CompanyPortStatisticsSection
          companyType="operator"
          companyId={operatorId}
          companyName={operatorLongName}
        />
      )}
      {tabSection === 7 && <FutureScheduleSection futurePortCallsQuery={futurePortCallsQuery} />}
    </Grid>
  );
}

OperatorProfileSection.propTypes = {
  overviewDataQuery: PropTypes.object,
  renderedOverviewMap: PropTypes.node
};

export default OperatorProfileSection;
